import { memo, ReactElement } from 'react'

import { Tooltip } from 'antd'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import { updateDspLineItem } from 'api/Cerebro/orgScoped/dspApi'
import {
    LINE_ITEM_BUDGET_MIN,
    LINE_ITEM_BUDGET_MAX,
    LINE_ITEM_BUDGET_STEP,
    LINE_ITEM_BUDGET_PRECISION,
} from 'const/lineItems'
import { DspLineItemGroupBy, CurrencyCode } from 'types'

import { BudgetField } from '../../SharedFields/BudgetField'

interface Props<RecordType> {
    dataIndex: string[]
    readOnly: boolean
    record: RecordType
}

function LineItemBudgetField<RecordType extends DspLineItemGroupBy>({
    dataIndex,
    readOnly = false,
    record,
}: Props<RecordType>): ReactElement {
    const { t } = useTranslation('table')

    const { line_item_id: lineItemId, line_item: lineItem } = record
    const currencyCode: CurrencyCode = lineItem.currency_code as CurrencyCode
    const getBudgetField = (): ReactElement => (
        <span>
            <BudgetField
                budgetMax={LINE_ITEM_BUDGET_MAX}
                budgetMin={LINE_ITEM_BUDGET_MIN}
                budgetPrecision={LINE_ITEM_BUDGET_PRECISION}
                budgetStep={LINE_ITEM_BUDGET_STEP}
                currencyCode={currencyCode}
                disabled={
                    !lineItem.user_can_manage ||
                    record.advertiser?.is_managed_account
                }
                fieldName="budget"
                fieldPath={dataIndex}
                readOnly={readOnly}
                record={record}
                updateRequestApi={updateDspLineItem}
                updateRequestData={lineItemId}
                updateRequestSuccessMesg={t(
                    'table:fields.lineItemBudget.successMsg',
                    'Successfully updated line item budget'
                )}
            />
        </span>
    )
    return record.advertiser?.is_managed_account ? (
        <Tooltip
            title={
                <span>
                    {t(
                        'table:fields.lineItemBudget.tooltip.managedExternally',
                        "This Line Item belongs to an externally managed account and can't be updated"
                    )}
                </span>
            }
            placement="topRight"
        >
            {getBudgetField()}
        </Tooltip>
    ) : (
        getBudgetField()
    )
}

export default memo(LineItemBudgetField, isEqual) as typeof LineItemBudgetField
